import { useState } from 'react'

import { Button } from '../../components/ui/Button'
import { Callout } from '../../components/ui/Callout'

export interface RecoveryCodesProps {
  codes: string[]
  /** Called once the user confirms they have stored them. */
  onAcknowledge: () => void
}

/**
 * The one-time display of a fresh set of recovery codes.
 *
 * They are stored hashed server-side, so this is genuinely the only time they
 * can ever be shown — there is no endpoint that could redisplay them, and
 * losing them means regenerating. The UI therefore refuses to be dismissed by
 * accident: the acknowledgement is an explicit checkbox rather than a close
 * button someone taps to get on with their day.
 *
 * A recovery code is a full authentication factor. Whoever holds one signs in
 * without the phone, which is why they are presented with the weight of a
 * password rather than the weight of a confirmation toast.
 */
export function RecoveryCodes({ codes, onAcknowledge }: RecoveryCodesProps) {
  const [acknowledged, setAcknowledged] = useState(false)
  const [copied, setCopied] = useState(false)

  const asText = codes.join('\n')

  async function copyAll() {
    try {
      await navigator.clipboard.writeText(asText)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access is refused in some browsers and any insecure origin.
      // The codes are already on screen and downloadable, so this is a
      // convenience failing, not a dead end — say nothing and let the user
      // select the text.
    }
  }

  function download() {
    // A Blob and an object URL rather than a data: URI — a data: URI puts the
    // codes in the URL, where they land in history and in any logging proxy
    // that records navigations.
    const blob = new Blob([`${asText}\n`], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'ledger-recovery-codes.txt'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="lg-stack">
      <Callout tone="warning" title="Save these now — they cannot be shown again" live="status">
        Each code works once, and any of them signs you in without your phone. Store them where you
        keep passwords, not in the same place as your authenticator app.
      </Callout>

      {/* A list, not a grid of divs: it is an enumerable set of ten items and
          a screen reader should announce it as such. */}
      <ul className="lg-codes" aria-label="Your recovery codes">
        {codes.map((code) => (
          <li key={code} className="lg-codes__item">
            {code}
          </li>
        ))}
      </ul>

      <div className="lg-row">
        <Button onClick={copyAll}>{copied ? 'Copied' : 'Copy all'}</Button>
        <Button onClick={download}>Download .txt</Button>
        {/* aria-live so the copy confirmation is announced, not just seen. */}
        <span className="lg-sr-only" role="status">
          {copied ? 'Recovery codes copied to the clipboard' : ''}
        </span>
      </div>

      <label className="lg-row lg-check">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
        />
        <span>I have saved these codes somewhere safe.</span>
      </label>

      <div>
        <Button variant="primary" disabled={!acknowledged} onClick={onAcknowledge}>
          Done
        </Button>
      </div>
    </div>
  )
}
